import type { VideoOperationResult, VideoTypicalDuration } from '../../../domain';

/**
 * What the Animation Lab says while a clip renders — only things we actually know.
 * fal reports a state (queued / running / done) and a queue position, never a percentage,
 * so there is no "% done" and no countdown: the time shown is time elapsed since submit,
 * and the expectation comes from how long recent renders on the same model really took.
 */

/** Elapsed time as m:ss (or h:mm:ss past an hour). */
export function formatElapsed(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

/** "usually 40–90s", "usually 1–3 min", "usually about 2 min". */
export function formatTypical(range: VideoTypicalDuration): string {
  const { lowSeconds: low, highSeconds: high } = range;
  if (high < 60) return low === high ? `usually about ${high}s` : `usually ${low}–${high}s`;
  const lowMin = Math.max(1, Math.floor(low / 60));
  const highMin = Math.max(lowMin, Math.ceil(high / 60));
  return lowMin === highMin ? `usually about ${highMin} min` : `usually ${lowMin}–${highMin} min`;
}

/** The headline: where the job is, and for how long. */
export function renderHeadline(job: VideoOperationResult | undefined, elapsedSeconds: number): string {
  const elapsed = `${formatElapsed(elapsedSeconds)} elapsed`;
  if (job?.status === 'Queued') {
    const pos = job.queuePosition;
    const where = pos == null ? '' : pos <= 0 ? ' · next in line' : ` · ${pos} ahead`;
    return `Queued${where} · ${elapsed}`;
  }
  return `Rendering · ${elapsed}`;
}

/** Past this, the render is slower than recent ones on the model and we say so. */
export function isSlowerThanUsual(range: VideoTypicalDuration | null | undefined, elapsedSeconds: number): boolean {
  if (!range) return false;
  return elapsedSeconds > Math.max(range.highSeconds * 1.5, range.highSeconds + 30);
}

/** The second line: what to expect, from real history when there is enough of it. */
export function renderExpectation(job: VideoOperationResult | undefined, elapsedSeconds: number): string {
  const range = job?.typicalDuration;
  if (isSlowerThanUsual(range, elapsedSeconds)) {
    return 'Taking longer than usual. Keep this page open: leaving it cancels the render.';
  }
  if (range) return `This model takes ${formatTypical(range)}. Keep this page open until it finishes.`;
  return 'Video renders usually take a few minutes. Keep this page open until it finishes.';
}

/** Shown once polling pauses after a very long render. */
export const RENDER_STALLED_TEXT =
  'Still not finished after an hour, so we stopped checking. Press Check now to ask again; leaving this page cancels the render.';

/** Seconds since the job started: the server's start time, else when this page saw it begin. */
export function elapsedSince(startedAt: string | null | undefined, fallbackMs: number, nowMs: number): number {
  const parsed = startedAt ? Date.parse(startedAt) : NaN;
  const start = Number.isFinite(parsed) ? parsed : fallbackMs;
  return Math.max(0, (nowMs - start) / 1000);
}
